import { ListEventRecordsQueryControllerListTab } from "@/generated/model/listEventRecordsQueryControllerListTab";
import type { SortDir } from "@/lib/category-display";

/**
 * The three Events Hub tabs (NAU-341). Open is the default: it holds the records
 * a manager is actively working, which is what the page is for.
 *
 * These names are the API's tab values too, so no UI↔API mapping is needed.
 *
 * `EventsHubTab` / `VALID_TABS` / `DEFAULT_TAB` are public because
 * lib/dashboard/category-map.ts builds its `CategoryDestination` arm from them
 * (NAU-370), so the dashboard's Events row cannot name a tab this page rejects.
 */
export type EventsHubTab = "open" | "awaiting-acknowledgement" | "history";

export const VALID_TABS: EventsHubTab[] = [
  "open",
  "awaiting-acknowledgement",
  "history",
];

export const DEFAULT_TAB: EventsHubTab = "open";

/**
 * The API tab value for a UI tab. They are the same strings, but going through the
 * generated enum means a server-side rename fails the build here rather than
 * silently returning the wrong rows.
 */
export function apiTabFor(
  tab: EventsHubTab,
): ListEventRecordsQueryControllerListTab {
  switch (tab) {
    case "open":
      return ListEventRecordsQueryControllerListTab.open;
    case "awaiting-acknowledgement":
      return ListEventRecordsQueryControllerListTab["awaiting-acknowledgement"];
    case "history":
      return ListEventRecordsQueryControllerListTab.history;
  }
}

/** Sort columns the API accepts. Must match its allow-list. */
export type EventsHubSortColumn =
  | "event"
  | "category"
  | "date"
  | "reportedBy"
  | "status";

export const PAGE_SIZE = 10;

/**
 * Default sort per tab. History leads with the most recently closed; the working
 * tabs lead with the most recently reported.
 */
export const DEFAULT_SORT: Record<
  EventsHubTab,
  { column: EventsHubSortColumn; dir: SortDir }
> = {
  open: { column: "date", dir: "desc" },
  "awaiting-acknowledgement": { column: "date", dir: "desc" },
  history: { column: "date", dir: "desc" },
};

/** What the pagination footer calls the rows. */
export function itemNounFor(tab: EventsHubTab): string {
  return tab === "awaiting-acknowledgement"
    ? "events awaiting review"
    : "events";
}

/** Empty-state copy per tab, so each explains what would put a row here. */
export const EMPTY_MESSAGE: Record<EventsHubTab, string> = {
  open: "No events are currently in progress.",
  "awaiting-acknowledgement": "No events are waiting to be acknowledged.",
  history: "No resolved or archived events yet.",
};

/** Sentinel select value meaning "no filter applied". */
export const ALL_FILTER_VALUE = "ALL";

/** The filter state the Reset filters button clears. */
export interface EventsHubFilters {
  categoryId?: string;
  eventTypeId?: string;
}

/**
 * The cleared filter state. Location is absent by design: that control selects the
 * nursery for the whole session (the same state the top-bar switcher writes), so
 * resetting filters must not relocate the user — the convention lib/task-manager.ts
 * documents.
 */
export const EMPTY_FILTERS: EventsHubFilters = {
  categoryId: undefined,
  eventTypeId: undefined,
};

/** Whether anything is filtered, so Reset can be disabled when it would no-op. */
export function hasActiveFilters(filters: EventsHubFilters): boolean {
  return !!filters.categoryId || !!filters.eventTypeId;
}

/**
 * "2 to read & sign" — how many people were asked to read a record and have not
 * yet confirmed.
 *
 * On screen to explain why a row is where it is. A record carrying outstanding
 * requests is listed under Awaiting acknowledgement as well as under its own
 * status, so the Hub shows rows reading "In progress" — and rows that never
 * needed acknowledging at all — in a tab named for something else. Without this
 * the tab looks broken; with it the second reason is visible.
 *
 * Deliberately not the word "acknowledgement": these confirmations do not
 * acknowledge the record, and the tab above them already spends that word on the
 * thing that does. "Read & sign" is the wording the people being chased see on
 * their own To do list.
 */
export function readAndSignLabel(outstandingCount: number): string | null {
  if (outstandingCount <= 0) {
    return null;
  }
  return `${outstandingCount} to read & sign`;
}
